import { TranslocoPipe, provideTranslocoScope, translate } from '@jsverse/transloco';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatRadioModule } from '@angular/material/radio';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoCurrencyPipe } from '@jsverse/transloco-locale';
import {
  AgentiService,
  AgenteLookupItem,
  CustomersFilter,
  CustomersFilterDialogComponent,
  CustomersFilterDialogData,
  IconAlert,
  IspettoreLookupItem,
  IspettoriService,
  TipoZonaLookupItem,
  TipoZonaService,
  ZoneItem,
  ZoneItemService,
} from 'customers';
import { HeaderService, Q8Breakpoints } from 'core';
import {
  LoadingSpinner,
  SortColumn,
  TableColumnInfo,
  TableView,
  TypeOfColumn,
} from 'design-system';
import { debounceTime, finalize, map, Observable, of, startWith, switchMap } from 'rxjs';

import { OrderProposal } from '../../interfaces/order-proposal.model';
import { OrderProposalService } from '../../services/order-proposal.service';
import { proposalNeedsCodeChoice } from '../../utils/order.utils';
import { CustomerBlockedDialog } from './customer-blocked-dialog/customer-blocked-dialog';
import { CustomerRadioCell } from './customer-radio-cell/customer-radio-cell';

const SORT_PROPERTY_MAP: Record<string, string> = {
  ragioneSociale: 'RagioneSociale',
  localita: 'Localita',
  zonaAgente: 'NomeZona',
  esposizione: 'Esposizione.Valore',
  affidamento: 'Affidamento.Valore',
  fatturatoRolling12m: 'FatturatoRolling12m',
  dataUltimaFattura: 'DataUltimaFattura',
  alert: 'Alert.Valore',
};

@Component({
  providers: [provideTranslocoScope('orders')],
  selector: 'ord-select-customer',
  imports: [TranslocoPipe,
    AsyncPipe,
    IconAlert,
    LoadingSpinner,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatRadioModule,
    ReactiveFormsModule,
    TableView,
    TranslocoCurrencyPipe,
  ],
  templateUrl: './select-customer.html',
  styleUrl: './select-customer.scss',
})
export class SelectCustomer implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('loadMoreTrigger')
  private loadMoreTrigger!: ElementRef<HTMLElement>;

  readonly searchControl = new FormControl('', { nonNullable: true });
  readonly desktopPageSize = 10;

  customers: any[] = [];
  customersLoading = false;
  totalItems = 0;
  desktopCustomers: any[] = [];
  desktopCustomersLoading = false;
  desktopTotalItems = 0;
  desktopPageIndex = 0;
  currentSortColumns: SortColumn[] = [];
  isDesktop = false;
  filtersOpen = false;
  filtersForm!: FormGroup;
  filteredTipoZona!: Observable<TipoZonaLookupItem[]>;
  filteredZona!: Observable<ZoneItem[]>;
  filteredAgente!: Observable<AgenteLookupItem[]>;
  filteredIspettore!: Observable<IspettoreLookupItem[]>;
  zoneOptions: ZoneItem[] = [];
  tipoZonaOptions: TipoZonaLookupItem[] = [];
  agenteOptions: AgenteLookupItem[] = [];
  ispettoreOptions: IspettoreLookupItem[] = [];

  selectedCustomer: any | null = null;
  confirmingSelection = false;

  readonly columnsInfo: TableColumnInfo[] = [
    {
      columnNameDef: 'selection',
      label: '',
      type: TypeOfColumn.COMPONENT,
      component: CustomerRadioCell,
    },
    {
      columnNameDef: 'ragioneSociale',
      get label() { return translate<string>('orders.ui.ragioneSociale'); },
      type: TypeOfColumn.TEXT,
      labelOnOneLine: true,
      sortable: true,
    },
    {
      columnNameDef: 'localita',
      get label() { return translate<string>('orders.ui.localita'); },
      type: TypeOfColumn.TEXT,
      sortable: true,
    },
    {
      columnNameDef: 'zonaAgente',
      get label() { return translate<string>('orders.ui.zonaAgente'); },
      type: TypeOfColumn.TEXT,
      sortable: true,
    },
    {
      columnNameDef: 'esposizione',
      get label() { return translate<string>('orders.ui.esposizione2'); },
      type: TypeOfColumn.CURRENCY,
      labelOnOneLine: true,
      sortable: true,
      cellClass: row => this.getCustomerStatusClass(row.esposizioneStato),
    },
    {
      columnNameDef: 'affidamento',
      get label() { return translate<string>('orders.ui.affidamento'); },
      type: TypeOfColumn.CURRENCY,
      labelOnOneLine: true,
      sortable: true,
      cellClass: row => this.getCustomerStatusClass(row.affidamentoStato),
    },
    {
      columnNameDef: 'fatturatoRolling12m',
      get label() { return translate<string>('orders.ui.fatturato12Mesi'); },
      type: TypeOfColumn.CURRENCY,
      sortable: true,
    },
    {
      columnNameDef: 'dataUltimaFattura',
      get label() { return translate<string>('orders.ui.ultimaFattura'); },
      type: TypeOfColumn.DATE,
      dateFormat: 'dd/MM/yyyy',
      sortable: true,
    },
    {
      columnNameDef: 'alert',
      get label() { return translate<string>('orders.ui.alert'); },
      type: TypeOfColumn.COMPONENT,
      component: IconAlert,
      sortable: true,
    },
  ];

  private filters: CustomersFilter = {};
  private pageIndex = 0;
  private readonly pageSize = 20;
  private customersInitialized = false;
  private desktopCustomersInitialized = false;
  private customersLoadRequestId = 0;
  private desktopCustomersLoadRequestId = 0;
  private customersObserver?: IntersectionObserver;
  private filtersDialogRef?: MatDialogRef<CustomersFilterDialogComponent>;
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly proposalService = inject(OrderProposalService);
  private readonly headerService = inject(HeaderService);
  private readonly http = inject(HttpClient);
  private readonly zoneService = inject(ZoneItemService);
  private readonly tipoZonaService = inject(TipoZonaService);
  private readonly agentiService = inject(AgentiService);
  private readonly ispettoriService = inject(IspettoriService);

  ngOnInit(): void {
    this.headerService.setTitle('orders.ui.nuovaPropostaDOrdine');

    // Il cliente della proposta è già selezionato: riconfermarlo non cambia nulla
    this.proposalService.ensure(this.route.snapshot.queryParamMap.get('id') ?? '').subscribe(proposal => {
      if (proposal.idBusinessPartner && !this.selectedCustomer) {
        this.selectedCustomer = this.proposalSelection(proposal);
      }
    });

    this.initializeFilters();
    this.loadFilterOptions();

    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reloadCustomers());

    this.breakpointObserver
      .observe([`(max-width: ${Q8Breakpoints.desktop - 1}px)`])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ matches }) => {
        if (matches) {
          this.filtersOpen = false;
          this.headerService.setActions([{
            get label() { return translate<string>('orders.ui.mostraFiltri'); },
            icon: 'fa-filter',
            action: () => this.openFiltersDialog(),
          }]);
          return;
        }

        this.headerService.removeActions();
      });

    this.breakpointObserver
      .observe([`(min-width: ${Q8Breakpoints.desktop}px)`])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ matches }) => {
        this.isDesktop = matches;

        if (matches) {
          if (!this.desktopCustomersInitialized) {
            this.loadDesktopCustomers();
          }
          return;
        }

        if (!this.customersInitialized) {
          this.loadCustomers(0, false);
        }
        this.refreshCustomersObserver();
      });
  }

  ngAfterViewInit(): void {
    this.customersObserver = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.loadNextCustomersPage();
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0 },
    );

    this.customersObserver.observe(this.loadMoreTrigger.nativeElement);
  }

  ngOnDestroy(): void {
    this.customersObserver?.disconnect();
    this.customersObserver = undefined;
    this.filtersDialogRef?.close();
    this.headerService.removeTitle();
    this.headerService.removeActions();
  }

  get isInitialLoading(): boolean {
    return this.customersLoading && !this.customersInitialized;
  }

  get isLoadingMore(): boolean {
    return this.customersLoading && this.customersInitialized;
  }

  get customersEmpty(): boolean {
    return this.customersInitialized && !this.customersLoading && this.customers.length === 0;
  }

  get desktopCustomersEmpty(): boolean {
    return this.desktopCustomersInitialized &&
      !this.desktopCustomersLoading &&
      this.desktopCustomers.length === 0;
  }

  getCustomerStatusClass(status?: string | null): string {
    switch (status) {
      case 'Critical':
        return 'currency-red';
      case 'Warning':
        return 'currency-yellow';
      default:
        return 'currency-green';
    }
  }

  onDesktopPageChange(event: PageEvent): void {
    this.desktopPageIndex = event.pageIndex;
    this.loadDesktopCustomers();
  }

  onDesktopSortChange(sortColumns: SortColumn[]): void {
    this.currentSortColumns = sortColumns;
    this.desktopPageIndex = 0;
    this.loadDesktopCustomers();
  }

  openFiltersDialog(): void {
    if (this.filtersDialogRef) {
      return;
    }

    const data: CustomersFilterDialogData = {
      value: { ...this.filters },
      options: {
        localita: [],
        zona: this.zoneOptions,
        tipoZona: this.tipoZonaOptions,
        agente: this.agenteOptions,
        ispettore: this.ispettoreOptions,
      },
    };

    this.filtersDialogRef = this.dialog.open(CustomersFilterDialogComponent, { data });
    this.filtersDialogRef.afterClosed().subscribe(result => {
      this.filtersDialogRef = undefined;

      if (result?.action === 'reset') {
        this.resetFilters();
      } else if (result?.action === 'apply') {
        this.filtersForm.patchValue(result.value);
        this.applyFilters();
      }
    });
  }

  openNewCustomer(): void {
    void this.router.navigate(['/new-order-proposal/new-customer'], {
      queryParams: { id: this.route.snapshot.queryParamMap.get('id') },
    });
  }

  confirmSelection(): void {
    if (!this.selectedCustomer || this.confirmingSelection) {
      return;
    }

    const { idBusinessPartner } = this.selectedCustomer;
    if (idBusinessPartner === this.proposalService.proposal()?.idBusinessPartner) {
      this.navigateToProposal();
      return;
    }

    const previous = this.proposalService.proposal()!;
    this.confirmingSelection = true;

    // Il cliente si scrive subito sulla proposta; se servono, i codici si chiedono sopra la lista.
    // «Annulla» sui codici rimette il cliente di prima, o nessuno, e la lista torna sulla scelta di prima
    this.proposalService.setCustomer(idBusinessPartner).pipe(
      switchMap(proposal => proposalNeedsCodeChoice(proposal) ? this.proposalService.chooseCustomerCodes() : of(proposal)),
      switchMap(proposal => proposal ? of(proposal) : this.proposalService.restoreCustomer(previous).pipe(map(() => null))),
      finalize(() => this.confirmingSelection = false),
    ).subscribe({
      next: proposal => {
        if (proposal) {
          this.navigateToProposal();
        } else {
          this.selectedCustomer = this.proposalSelection(previous);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.selectedCustomer = this.proposalSelection(previous);
        // 400: blocco attivo o nessun codice valido, col motivo del backend
        if (error.status === 400) {
          this.dialog.open(CustomerBlockedDialog, {
            width: '400px',
            data: { message: error.error?.detail ?? '' },
          });
        }
      },
    });
  }

  toggleFilters(): void {
    this.filtersOpen = !this.filtersOpen;
  }

  applyFilters(): void {
    this.filters = this.filtersForm.getRawValue();
    this.reloadCustomers();
  }

  resetFilters(): void {
    this.filtersForm.reset({ newCustomer: false });
    this.applyFilters();
  }

  clearFilterField(controlName: string): void {
    this.filtersForm.get(controlName)?.setValue('');
  }

  onTipoZonaSelected(tipoZona: TipoZonaLookupItem): void {
    this.filtersForm.patchValue({ tipoZona: tipoZona.descrizione, tipoZonaId: tipoZona.id });
  }

  onZonaSelected(zone: ZoneItem): void {
    this.filtersForm.patchValue({ zona: zone.nome, zonaId: zone.id });
  }

  onAgenteSelected(agente: AgenteLookupItem): void {
    this.filtersForm.patchValue({ agente: agente.label, agenteId: agente.value });
  }

  onIspettoreSelected(ispettore: IspettoreLookupItem): void {
    this.filtersForm.patchValue({ ispettore: ispettore.label, ispettoreId: ispettore.value });
  }

  private proposalSelection(proposal: OrderProposal): { idBusinessPartner: string; ragioneSociale: string | null } | null {
    return proposal.idBusinessPartner ? { idBusinessPartner: proposal.idBusinessPartner, ragioneSociale: proposal.ragioneSociale } : null;
  }

  private navigateToProposal(): void {
    void this.router.navigate(['/new-order-proposal'], {
      queryParams: { id: this.proposalService.proposal()?.id },
      replaceUrl: true,
    });
  }

  private initializeFilters(): void {
    this.filtersForm = this.formBuilder.group({
      tipoZona: [''],
      tipoZonaId: [''],
      zona: [''],
      zonaId: [''],
      agente: [''],
      agenteId: [''],
      ispettore: [''],
      ispettoreId: [''],
      dataUltimaFattura: [null as Date | null],
      newCustomer: [false],
    });

    this.filteredTipoZona = this.optionsFor('tipoZona', search =>
      this.tipoZonaOptions.filter(option => option.descrizione.toLowerCase().includes(search)));
    this.filteredZona = this.optionsFor('zona', search =>
      this.zoneOptions.filter(option => option.nome.toLowerCase().includes(search)));
    this.filteredAgente = this.optionsFor('agente', search =>
      this.agenteOptions.filter(option => option.label.toLowerCase().includes(search)));
    this.filteredIspettore = this.optionsFor('ispettore', search =>
      this.ispettoreOptions.filter(option => option.label.toLowerCase().includes(search)));

    this.clearIdWhenLabelChanges('tipoZona', 'tipoZonaId', () =>
      this.tipoZonaOptions.find(option => option.id === this.filtersForm.get('tipoZonaId')!.value)?.descrizione);
    this.clearIdWhenLabelChanges('zona', 'zonaId', () =>
      this.zoneOptions.find(option => option.id === this.filtersForm.get('zonaId')!.value)?.nome);
    this.clearIdWhenLabelChanges('agente', 'agenteId', () =>
      this.agenteOptions.find(option => option.value === this.filtersForm.get('agenteId')!.value)?.label);
    this.clearIdWhenLabelChanges('ispettore', 'ispettoreId', () =>
      this.ispettoreOptions.find(option => option.value === this.filtersForm.get('ispettoreId')!.value)?.label);
  }

  private optionsFor<T>(controlName: string, filterFn: (search: string) => T[]): Observable<T[]> {
    const control = this.filtersForm.get(controlName)!;
    return control.valueChanges.pipe(
      startWith(control.value ?? ''),
      map(value => filterFn((value ?? '').toString().toLowerCase())),
    );
  }

  private clearIdWhenLabelChanges(
    labelControlName: string,
    idControlName: string,
    selectedLabel: () => string | undefined,
  ): void {
    this.filtersForm.get(labelControlName)!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (selectedLabel() !== value) {
          this.filtersForm.get(idControlName)!.setValue('', { emitEvent: false });
        }
      });
  }

  private loadFilterOptions(): void {
    this.zoneService.getZoneItem().subscribe(response => {
      this.zoneOptions = response.items ?? [];
      this.refreshOptions('zona');
    });
    this.tipoZonaService.getTipoZone().subscribe(items => {
      this.tipoZonaOptions = items ?? [];
      this.refreshOptions('tipoZona');
    });
    this.agentiService.getAgenti().subscribe(response => {
      this.agenteOptions = response.agenti ?? [];
      this.refreshOptions('agente');
    });
    this.ispettoriService.getIspettori().subscribe(response => {
      this.ispettoreOptions = response.ispettori ?? [];
      this.refreshOptions('ispettore');
    });
  }

  // Le opzioni arrivano dopo la creazione del form: rilancia il filtro per popolare l'autocomplete
  private refreshOptions(controlName: string): void {
    const control = this.filtersForm.get(controlName)!;
    control.setValue(control.value);
  }

  private reloadCustomers(): void {
    this.pageIndex = 0;
    this.desktopPageIndex = 0;

    if (this.isDesktop) {
      this.loadDesktopCustomers();
      return;
    }

    this.loadCustomers(0, false);
  }

  private buildSearchPayload(orderBy: { PropertyName: string; OrderType: string }[]): object {
    return {
      SearchText: { Text: this.searchControl.value },
      OrderBy: orderBy,
      searchObject: {
        ActiveCustomers: false,
        OrderInProgess: false,
        InvoicesExpiredLess30: false,
        InvoicesExpiredOver30: false,
        Agente: this.filters.agenteId ? [this.filters.agenteId] : [],
        Ispettore: this.filters.ispettoreId ? [this.filters.ispettoreId] : [],
        TipoZona: this.filters.tipoZonaId ? [this.filters.tipoZonaId] : [],
        ZonaId: this.filters.zonaId ? [this.filters.zonaId] : [],
        DataUltimaFattura: this.filters.dataUltimaFattura ?? null,
        NewCustomer: this.filters.newCustomer ?? false,
      },
    };
  }

  private loadNextCustomersPage(): void {
    if (
      this.isDesktop ||
      !this.customersInitialized ||
      this.customersLoading ||
      this.customers.length >= this.totalItems
    ) {
      return;
    }

    this.loadCustomers(this.pageIndex + 1, true);
  }

  private loadCustomers(pageIndex: number, append: boolean): void {
    const requestId = ++this.customersLoadRequestId;
    this.customersLoading = true;

    const params = {
      'Pager.PageSize': this.pageSize,
      'Pager.Page': pageIndex + 1,
    };

    this.http.post<any>('api/clienti/list', this.buildSearchPayload([]), { params }).pipe(
      finalize(() => {
        if (requestId === this.customersLoadRequestId) {
          this.customersLoading = false;
          this.refreshCustomersObserver();
        }
      }),
    ).subscribe({
      next: data => {
        if (requestId !== this.customersLoadRequestId) {
          return;
        }

        const loadedCustomers = this.mapCustomerItems(data.items);

        this.customers = append
          ? this.appendUniqueCustomers(loadedCustomers)
          : loadedCustomers;
        this.pageIndex = pageIndex;
        this.totalItems = data.totalItems;
        this.customersInitialized = true;
      },
      error: () => {
        if (requestId !== this.customersLoadRequestId) {
          return;
        }

        if (!append) {
          this.customers = [];
          this.totalItems = 0;
        }

        this.customersInitialized = true;
      },
    });
  }

  loadDesktopCustomers(): void {
    const requestId = ++this.desktopCustomersLoadRequestId;
    this.desktopCustomersLoading = true;

    const params = {
      'Pager.PageSize': this.desktopPageSize,
      'Pager.Page': this.desktopPageIndex + 1,
    };

    const payload = this.buildSearchPayload(
      this.currentSortColumns.map(sort => ({
        PropertyName: SORT_PROPERTY_MAP[sort.column] ?? sort.column,
        OrderType: sort.direction === 'desc' ? 'Desc' : 'Asc',
      })),
    );

    this.http.post<any>('api/clienti/list', payload, { params }).pipe(
      finalize(() => {
        if (requestId === this.desktopCustomersLoadRequestId) {
          this.desktopCustomersLoading = false;
        }
      }),
    ).subscribe({
      next: data => {
        if (requestId !== this.desktopCustomersLoadRequestId) {
          return;
        }

        this.desktopCustomers = this.mapCustomerItems(data.items);
        this.desktopTotalItems = data.totalItems;
        this.desktopCustomersInitialized = true;
      },
      error: () => {
        if (requestId !== this.desktopCustomersLoadRequestId) {
          return;
        }

        this.desktopCustomers = [];
        this.desktopTotalItems = 0;
        this.desktopCustomersInitialized = true;
      },
    });
  }

  private appendUniqueCustomers(loadedCustomers: any[]): any[] {
    const customerIds = new Set(this.customers.map(customer => customer.idPrimoCliente));
    const uniqueCustomers = [...this.customers];

    for (const customer of loadedCustomers) {
      if (customerIds.has(customer.idPrimoCliente)) {
        continue;
      }

      customerIds.add(customer.idPrimoCliente);
      uniqueCustomers.push(customer);
    }

    return uniqueCustomers;
  }

  private mapCustomerItems(items: any[]): any[] {
    return items.map((item: any) => ({
      ...item,
      zonaAgente: item.nomeAgente
        ? `${item.nomeZona}/${item.nomeAgente}`
        : `${item.nomeZona}`,
      esposizione: item.esposizione.valore,
      esposizioneStato: item.esposizione.stato,
      affidamento: item.affidamento.valore,
      affidamentoStato: item.affidamento.stato,
      isSelected: () => this.selectedCustomer?.idBusinessPartner === item.idBusinessPartner,
      select: () => this.selectedCustomer = item,
    }));
  }

  private refreshCustomersObserver(): void {
    if (!this.customersObserver || !this.loadMoreTrigger) {
      return;
    }

    requestAnimationFrame(() => {
      if (!this.customersObserver || !this.loadMoreTrigger) {
        return;
      }

      this.customersObserver.unobserve(this.loadMoreTrigger.nativeElement);
      this.customersObserver.observe(this.loadMoreTrigger.nativeElement);
    });
  }
}
